import { ref, type Ref } from 'vue';
import { Network } from '@capacitor/network';
import { useStore } from 'vue-livestore';
import { useAuth } from '@/features/auth/useAuth.js';
import { useSubscription } from '@/features/subscription/useSubscription.js';
import { checkClockDrift, verifyJws } from '@/core/useSigningEngine.js';
import { useAuditSigning } from '@/core/useAuditSigning.js';
import { events } from '@/livestore/schema.js';
import { useToast } from '@/core/useToast.js';
import { useOfflineQueue } from '@/features/sync/useOfflineQueue.js';
import { teamApi } from '@/api/index.js';

// TODO(0.8.x): Team management is a business-tier feature — move into a new plans/business/teams
// directory to organize by feature/plan association (can keep a "teams" subdirectory within it).

let _isOnline = true;
let _networkListener: any = null;

function ensureNetworkListener() {
  if (_networkListener) return;
  Network.getStatus()
    .then(s => {
      _isOnline = s.connected;
    })
    .catch(() => {});
  _networkListener = Network.addListener('networkStatusChange', s => {
    _isOnline = s.connected;
  });
}

function isOnline() {
  return _isOnline;
}

// Guards against registering the reconnect-drain listener more than once if useTeamSchedule() is
// called from multiple components — useOfflineQueue's own listener registration has no such
// guard, so this composable owns doing it once.
let _queueSetupDone = false;

interface AssignmentPayload {
  breakMinutes: number;
  endsAt: number;
  id?: string;
  memberEmail: string;
  shiftDate: string;
  startsAt: number;
  workplaceLabel: string;
}

async function doSignAndPostAssignment(
  tenantId: () => string,
  signAssignment: (args: {
    assignmentId: string;
    endsAt: number;
    memberEmail: string;
    startsAt: number;
    workplaceLabel: string;
  }) => Promise<string | null>,
  { breakMinutes, endsAt, id, memberEmail, shiftDate, startsAt, workplaceLabel }: AssignmentPayload,
): Promise<{ id: string; jws_signature: string; version: number }> {
  await checkClockDrift();
  const assignmentId = id ?? crypto.randomUUID();
  const jws = await signAssignment({
    assignmentId,
    endsAt,
    memberEmail,
    startsAt,
    workplaceLabel,
  });
  if (!jws) {
    throw new Error('Assignment signing failed.');
  }

  const result = await teamApi.pushAssignment({
    break_minutes: breakMinutes,
    ends_at: endsAt,
    id: assignmentId,
    jws,
    member_email: memberEmail,
    shift_date: shiftDate,
    starts_at: startsAt,
    tenant_id: tenantId(),
    workplace_label: workplaceLabel,
  });
  return { id: result.id ?? assignmentId, jws_signature: jws, version: result.version ?? 1 };
}

function buildOptimisticShiftRecord(
  localId: string,
  { breakMinutes, endsAt, memberEmail, shiftDate, startsAt, workplaceLabel }: AssignmentPayload,
  now: number,
) {
  return {
    assigned_to: memberEmail,
    assignment_version: 0,
    break_minutes: breakMinutes,
    created_at: now,
    date: shiftDate,
    ends_at: endsAt,
    id: localId,
    is_edited: 0,
    is_locked: 0,
    jws_signature: null,
    modified_timestamp: null,
    original_timestamp: null,
    source: 'team_assigned',
    starts_at: startsAt,
    updated_at: now,
    workplace_id: null,
    workplace_label: workplaceLabel,
  };
}

function buildAssignmentUpdatePayload(a: any, existingWorkplaceId: string | null | undefined) {
  return {
    assigned_to: a.member_email,
    assignment_version: a.version,
    break_minutes: a.break_minutes,
    ends_at: a.ends_at,
    id: a.id,
    is_locked: a.is_locked,
    jws_signature: a.jws_signature,
    source: 'team_assigned',
    starts_at: a.starts_at,
    updated_at: Date.now(),
    workplace_id: existingWorkplaceId ?? null,
    workplace_label: a.workplace_label,
  };
}

// pushAssignment and toggleLock both follow this exact shape: guard on auth, optimistic local
// commit, then try the online call / fall back to the offline queue / requeue on failure --
// extracted once so the online/offline/retry contract can't drift between the two call sites.
interface RunOptimisticSyncOpts {
  error: Ref<string | null>;
  guardMsg: string;
  isAuthenticated: Ref<boolean>;
  loading: Ref<boolean>;
  msgs: { offline: string; onlineSuccess: string; retry: string };
  onlineAction: () => Promise<void>;
  optimisticCommit: () => void;
  queueForRetry: () => void;
  toast: (msg: string, opts?: any) => void;
}

async function runOptimisticSync({
  error,
  guardMsg,
  isAuthenticated,
  loading,
  msgs,
  onlineAction,
  optimisticCommit,
  queueForRetry,
  toast,
}: RunOptimisticSyncOpts): Promise<boolean> {
  if (!isAuthenticated.value) {
    toast(guardMsg, { variant: 'error' });
    return false;
  }
  loading.value = true;
  error.value = null;
  optimisticCommit();
  try {
    if (isOnline()) {
      await onlineAction();
      toast(msgs.onlineSuccess, { variant: 'success' });
      return true;
    }
    // Offline: queue for later drain
    queueForRetry();
    toast(msgs.offline, { variant: 'info' });
    return true;
  } catch (e: any) {
    // Online call failed: queue for retry
    queueForRetry();
    error.value = e.message;
    toast(msgs.retry, { variant: 'info' });
    return true;
  } finally {
    loading.value = false;
  }
}

interface DoPushAssignmentOpts {
  args: AssignmentPayload;
  enqueueTeamItem: (data: any, kind: string) => void;
  error: Ref<string | null>;
  isAuthenticated: Ref<boolean>;
  loading: Ref<boolean>;
  signAndPostAssignment: (
    payload: AssignmentPayload,
  ) => Promise<{ id: string; jws_signature: string; version: number }>;
  store: any;
  toast: (msg: string, opts?: any) => void;
}

function doPushAssignment({
  args,
  enqueueTeamItem,
  error,
  isAuthenticated,
  loading,
  signAndPostAssignment,
  store,
  toast,
}: DoPushAssignmentOpts): Promise<boolean> {
  const localId = args.id ?? crypto.randomUUID();
  const payload = { ...args, id: localId };
  return runOptimisticSync({
    error,
    guardMsg: 'You must be signed in.',
    isAuthenticated,
    loading,
    msgs: {
      offline: 'Assignment saved offline. It will sync when you reconnect.',
      onlineSuccess: 'Assignment saved.',
      retry: 'Assignment queued. It will retry when you reconnect.',
    },
    onlineAction: async () => {
      const result = await signAndPostAssignment(payload);
      store.commit(
        events.scheduledShiftUpdated({
          assignment_version: result.version,
          id: result.id,
          jws_signature: result.jws_signature,
          updated_at: Date.now(),
        }),
      );
    },
    optimisticCommit: () =>
      store.commit(
        events.scheduledShiftCreated(buildOptimisticShiftRecord(localId, args, Date.now())),
      ),
    queueForRetry: () => enqueueTeamItem(payload, 'team_schedule'),
    toast,
  });
}

async function doPullAssignments(
  store: any,
  isAuthenticated: Ref<boolean>,
  loading: Ref<boolean>,
  error: Ref<string | null>,
  toast: (msg: string, opts?: any) => void,
  tenantId: () => string,
): Promise<void> {
  if (!isAuthenticated.value) return;
  loading.value = true;
  error.value = null;
  try {
    const { assignments } = await teamApi.pullAssignments(tenantId());
    for (const a of assignments) {
      await upsertPulledAssignment(store, toast, a);
    }
  } catch (e: any) {
    error.value = e.message;
  } finally {
    loading.value = false;
  }
}

async function upsertPulledAssignment(
  store: any,
  toast: (msg: string, opts?: any) => void,
  a: any,
): Promise<void> {
  if (a.jws_signature) {
    const verifyRes = await verifyJws(a.jws_signature);
    if (!verifyRes?.valid) {
      toast(`Skipped assignment for ${a.shift_date}: signature verification failed.`, {
        variant: 'error',
      });
      return;
    }
  }

  const existingRows = store.query((q: any) =>
    q.selectFrom('scheduledShifts').where('id', '=', a.id).limit(1).executeTakeFirst(),
  ) as Array<{ workplace_id?: string | null }>;
  const existing = existingRows[0];
  if (existing) {
    store.commit(
      events.scheduledShiftUpdated(buildAssignmentUpdatePayload(a, existing?.workplace_id)),
    );
  } else {
    store.commit(events.scheduledShiftCreated(buildAssignmentCreatePayload(a)));
  }
}

function buildAssignmentCreatePayload(a: any) {
  const now = Date.now();
  return {
    assigned_to: a.member_email,
    assignment_version: a.version,
    break_minutes: a.break_minutes,
    created_at: now,
    date: a.shift_date,
    ends_at: a.ends_at,
    id: a.id,
    is_edited: 0,
    is_locked: a.is_locked,
    jws_signature: a.jws_signature,
    modified_timestamp: null,
    original_timestamp: null,
    source: 'team_assigned',
    starts_at: a.starts_at,
    updated_at: now,
    workplace_id: null,
    workplace_label: a.workplace_label,
  };
}

export function useTeamSchedule() {
  const { store } = useStore();
  const { isAuthenticated } = useAuth();
  const { tenantId } = useSubscription();
  const { show: toast } = useToast();
  const { signAssignment } = useAuditSigning();
  const loading = ref(false);
  const error = ref<string | null>(null);

  ensureNetworkListener();

  // Calling useOfflineQueue() here (not just importing enqueue/drain) is required — it's what
  // initializes useOfflineQueue's internal store reference. Without at least one real call to
  // this hook, enqueue()/drain() throw "call setup() first" the first time they're used.
  const { enqueue: enqueueTeamItem, setup: setupTeamQueue } = useOfflineQueue({
    team_lock: drainTeamQueue,
    team_schedule: drainTeamQueue,
  });
  if (!_queueSetupDone) {
    _queueSetupDone = true;
    setupTeamQueue();
  }

  // ── Named helpers: sign + POST assignment ──────────────────────────────────
  function signAndPostAssignment(
    payload: AssignmentPayload,
  ): Promise<{ id: string; jws_signature: string; version: number }> {
    return doSignAndPostAssignment(tenantId, signAssignment, payload);
  }

  // ── Named helpers: POST lock toggle ────────────────────────────────────────
  async function postLockToggle(id: string, locked: boolean): Promise<void> {
    await teamApi.toggleLock(id, tenantId(), locked);
  }

  // ── Admin flow: sign the assignment, local-first ──────────────────────────
  function pushAssignment(args: AssignmentPayload): Promise<boolean> {
    return doPushAssignment({
      args,
      enqueueTeamItem,
      error,
      isAuthenticated,
      loading,
      signAndPostAssignment,
      store,
      toast,
    });
  }

  // ── Member flow: GET assignments, verify JWS, insert into local LiveStore ──
  function pullAssignments(): Promise<void> {
    return doPullAssignments(store, isAuthenticated, loading, error, toast, tenantId);
  }

  // ── Admin flow: toggle lock, local-first ──────────────────────────────────
  function toggleLock(id: string, locked: boolean): Promise<boolean> {
    return runOptimisticSync({
      error,
      guardMsg: 'You must be signed in.',
      isAuthenticated,
      loading,
      msgs: {
        offline: locked
          ? 'Lock queued. It will sync when you reconnect.'
          : 'Unlock queued. It will sync when you reconnect.',
        onlineSuccess: locked ? 'Assignment locked.' : 'Assignment unlocked.',
        retry: 'Lock change queued. It will retry when you reconnect.',
      },
      onlineAction: () => postLockToggle(id, locked),
      optimisticCommit: () =>
        store.commit(
          events.scheduledShiftUpdated({ id, is_locked: locked ? 1 : 0, updated_at: Date.now() }),
        ),
      queueForRetry: () => enqueueTeamItem({ id, locked }, 'team_lock'),
      toast,
    });
  }

  // ── Drain handler for queued team_schedule / team_lock items ───────────────
  async function drainTeamQueue(item: { bundleId: string; bundleJson: string }) {
    const data = JSON.parse(item.bundleJson);
    if (data.kind === 'team_lock') {
      await postLockToggle(data.id, data.locked);
    } else {
      await signAndPostAssignment(data);
    }
  }

  return { drainTeamQueue, error, loading, pullAssignments, pushAssignment, toggleLock };
}
